import React from "react";
import { Box, Skeleton, Stack, Typography } from "@mui/material";
import { tokens, fmtMoney, CodeChip, StatusPill } from "../../shared/clinical-design";

/** Tono según % capturado de la oportunidad anual. */
const capturedTone = (pct) => (pct >= 50 ? "ok" : pct >= 15 ? "warn" : "crit");

/**
 * Palancas de revenue ligadas a módulos operativos (E / N / G): oportunidad
 * anual, capturado YTD y barra de progreso sobria.
 *
 * @param {Object[]} levers - [{ key, label, module, description, annualOpportunity, capturedYtd, capturedPct }]
 * @param {boolean} [loading]
 */
export default function RevenueLeversPanel({ levers = [], loading = false }) {
  if (loading) return <Skeleton variant="rectangular" height={250} />;

  const totalOpportunity = levers.reduce((acc, l) => acc + l.annualOpportunity, 0);
  const totalCaptured = levers.reduce((acc, l) => acc + l.capturedYtd, 0);

  return (
    <Box>
      {levers.map((l, i) => (
        <Box
          key={l.key}
          sx={{ pb: 1.25, mb: 1.25, borderBottom: i < levers.length - 1 ? `1px solid ${tokens.line}` : "none" }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.25 }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: tokens.ink }}>
              {l.label}
            </Typography>
            <CodeChip code={l.module} />
            <Box sx={{ flex: 1 }} />
            <StatusPill
              tone={capturedTone(l.capturedPct)}
              label={`${l.capturedPct.toFixed(0)}% captured`}
              dense
            />
          </Stack>
          <Typography sx={{ fontSize: 12, color: tokens.ink3, mb: 0.75 }}>
            {l.description}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{ flex: 1, height: 8, bgcolor: tokens.surface2, borderRadius: "2px", overflow: "hidden" }}>
              <Box
                sx={{
                  width: `${Math.min(l.capturedPct, 100)}%`,
                  height: "100%",
                  bgcolor: tokens.primary,
                }}
              />
            </Box>
            <Typography
              sx={{
                fontFamily: tokens.fontMono,
                fontVariantNumeric: "tabular-nums",
                fontSize: 12.5,
                color: tokens.ink2,
                whiteSpace: "nowrap",
              }}
            >
              {fmtMoney(l.capturedYtd)} / {fmtMoney(l.annualOpportunity)}
            </Typography>
          </Stack>
        </Box>
      ))}

      <Stack direction="row" alignItems="baseline" spacing={1} sx={{ pt: 0.25 }}>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 500,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: tokens.ink3,
          }}
        >
          Total annual opportunity
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Typography
          sx={{
            fontFamily: tokens.fontMono,
            fontVariantNumeric: "tabular-nums",
            fontSize: 16,
            fontWeight: 600,
            color: tokens.primary700,
          }}
        >
          {fmtMoney(totalOpportunity)}
        </Typography>
        <Typography sx={{ fontFamily: tokens.fontMono, fontSize: 12, color: tokens.ink3 }}>
          ({fmtMoney(totalCaptured)} YTD)
        </Typography>
      </Stack>
    </Box>
  );
}
